import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Tooltip,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import RefreshIcon from "@material-ui/icons/Refresh";
import BarChartIcon from "@material-ui/icons/BarChart";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from "recharts";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  rootPaper: {
    padding: theme.spacing(2.5),
    borderRadius: 14,
    border: "1px solid #e2e8f0",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.03)",
    backgroundColor: "#ffffff",
    marginBottom: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1.5),
    flexWrap: "wrap",
    gap: 8,
  },
  title: {
    fontWeight: 700,
    fontSize: "1.15rem",
    display: "flex",
    alignItems: "center",
    gap: 10,
    color: "#0f172a",
  },
  chartContainer: {
    height: 340,
    width: "100%",
    marginTop: theme.spacing(1),
  },
}));

const CustomBarTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const total = payload.reduce((acc, curr) => acc + (curr.value || 0), 0);
    return (
      <Paper
        style={{
          padding: "10px 14px",
          backgroundColor: "#ffffff",
          border: "1px solid #cbd5e1",
          borderRadius: 8,
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}
      >
        <Typography style={{ fontWeight: 700, fontSize: "0.9rem", color: "#0f172a", marginBottom: 6 }}>
          📱 Conexão: {label}
        </Typography>
        <Typography style={{ fontSize: "0.82rem", color: "#334155", fontWeight: 600, marginBottom: 6 }}>
          Total de Conversas Ativas: <strong>{total}</strong>
        </Typography>
        {payload.map((entry, index) => (
          <div key={`item-${index}`} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.78rem", color: "#475569", marginBottom: 2 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: entry.color, display: "inline-block" }} />
            <span>{entry.name}: <strong>{entry.value}</strong></span>
          </div>
        ))}
      </Paper>
    );
  }
  return null;
};

const ConnectionsTicketsDashboard = () => {
  const classes = useStyles();
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatsapp/tickets-metrics");
      setMetrics(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erro ao carregar métricas das conexões:", err);
      toastError(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchMetrics();
    // Auto-refresh sutil a cada 45 segundos
    const interval = setInterval(fetchMetrics, 45000);
    return () => clearInterval(interval);
  }, []);

  const chartData = metrics.map((item) => ({
    name: item.whatsappName,
    "Com IA": item.withAi,
    "Com Humano": item.withHuman,
    "Fixadas": item.pinned,
    total: item.totalActive,
  }));

  const totalGeralAtivas = metrics.reduce((acc, curr) => acc + curr.totalActive, 0);

  return (
    <Paper className={classes.rootPaper} variant="outlined">
      {/* CABEÇALHO */}
      <div className={classes.header}>
        <div className={classes.title}>
          <BarChartIcon style={{ color: "#4f46e5", fontSize: "1.5rem" }} />
          <span>Dimensionamento de Conversas por Conexão</span>
          <Chip
            size="small"
            label={`${totalGeralAtivas} conversas ativas no momento`}
            style={{
              backgroundColor: "#ede9fe",
              color: "#5b21b6",
              fontWeight: 700,
              fontSize: "0.75rem",
            }}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Typography variant="caption" style={{ color: "#64748b", fontWeight: 500 }}>
            Atualização em tempo real (45s)
          </Typography>
          <Tooltip title="Atualizar Agora">
            <IconButton
              size="small"
              onClick={fetchMetrics}
              disabled={loading}
              style={{ border: "1px solid #e2e8f0" }}
            >
              {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {loading && metrics.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={5}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <div className={classes.chartContainer}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 10, right: 25, left: -10, bottom: 25 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis
                dataKey="name"
                tick={{ fill: "#475569", fontSize: 12, fontWeight: 600 }}
                axisLine={{ stroke: "#cbd5e1" }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: "#64748b", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
              />
              <RechartsTooltip content={<CustomBarTooltip />} />
              <Legend
                verticalAlign="top"
                align="right"
                wrapperStyle={{ paddingBottom: 15, fontSize: "0.82rem", fontWeight: 600 }}
              />
              <Bar
                dataKey="Com IA"
                stackId="a"
                fill="#22c55e"
                radius={[0, 0, 0, 0]}
                maxBarSize={60}
              />
              <Bar
                dataKey="Com Humano"
                stackId="a"
                fill="#f97316"
                radius={[0, 0, 0, 0]}
                maxBarSize={60}
              />
              <Bar
                dataKey="Fixadas"
                stackId="a"
                fill="#9333ea"
                radius={[6, 6, 0, 0]}
                maxBarSize={60}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Paper>
  );
};

export default ConnectionsTicketsDashboard;
